import { useState } from 'react';
import { Button, Chip, IconButton, Tooltip } from '@mui/material';
import { DoneAll, MarkEmailRead } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { notificationsApi, type Notification } from '../../api/engine.api';

const PRIORITY: Record<string, 'default' | 'info' | 'warning' | 'error'> = { low: 'default', normal: 'info', high: 'warning', urgent: 'error' };

export function NotificationsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({ queryKey: ['notifications', page], queryFn: () => notificationsApi.list({ page, limit: 25 }) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['notifications'] }); qc.invalidateQueries({ queryKey: ['notif-unread'] }); qc.invalidateQueries({ queryKey: ['notif-list'] }); };

  const markRead = useMutation({ mutationFn: (id: number) => notificationsApi.markRead(id), onSuccess: refresh });
  const markAll = useMutation({ mutationFn: () => notificationsApi.markAllRead(), onSuccess: refresh });

  const columns: Column<Notification>[] = [
    { key: 'title', label: 'Title', render: (r) => <span style={{ fontWeight: r.isRead ? 400 : 700 }}>{r.title}</span> },
    { key: 'body', label: 'Detail' },
    { key: 'priority', label: 'Priority', render: (r) => <Chip size="small" label={r.priority} color={PRIORITY[r.priority] ?? 'default'} /> },
    { key: 'isRead', label: 'Status', render: (r) => (r.isRead ? <Chip size="small" label="read" /> : <Chip size="small" color="info" label="unread" />) },
    { key: 'createdAt', label: 'When', render: (r) => new Date(r.createdAt).toLocaleString() },
  ];

  return (
    <>
      <PageHeader title="Notifications" subtitle="Your in-app notification center." action={<Button startIcon={<DoneAll />} variant="outlined" onClick={() => markAll.mutate()}>Mark all read</Button>} />
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={25} total={data?.meta?.total ?? 0} onPageChange={setPage} onLimitChange={() => undefined} rowKey={(r) => r.id}
        emptyTitle="No notifications"
        actions={(r) => (!r.isRead ? <Tooltip title="Mark read"><IconButton size="small" onClick={() => markRead.mutate(r.id)}><MarkEmailRead fontSize="small" /></IconButton></Tooltip> : null)}
      />
    </>
  );
}
